import axios from "../../../utils/axios";
import { handleRemoveEmptyParamFromQueryParams } from "../../../utils/helpers";
import {
    IClientFilterTypes,
    IUpdateClientOnIntake,
} from "../../../utils/types/client";
import {
    IAddClientFlag,
    IAddClientSession,
    ICardFilterTypes,
    IClientDocumentsListFilters,
    IClientEditInsuranceDetails,
    IClientIntakeImportDiagnosisPayload,
    IClientMemoListFilterTypes,
    ICollectionsNoteFilterTypes,
    IClientNewInsuranceDetails,
    IClientNotesListFilterTypes,
    IClientProviderHistoryListFilters,
    ICallHistoryFilters,
    INewCommunicationNoteRecord,
    IDeleteCardsPayload,
    IDeleteClientSession,
    IDeleteInsuranceInformationPayload,
    IEditClientFee,
    IEditClientSession,
    IGeneratePaymentLinkPayload,
    IInsuranceProvidersFilterTypes,
    INewCreditCardDetails,
    INewMemo,
    ISmsHistoryFilters,
    IValidateCardPayload,
    ISetPreferredCardPayload,
    ClientTagKey,
    IClientTagActivityFilterTypes,
} from "../types/clients.types";

// Endpoint to add client payment card
export async function addPaymentCard(
    clientId: string,
    data: INewCreditCardDetails
) {
    const response = await axios.post(
        `/client/${clientId}/payment-cards`,
        data
    );
    return response.data;
}

// Endpoint to add client insurance details
export async function addClientInsuranceDetails(
    clientId: string,
    data: IClientNewInsuranceDetails
) {
    const response = await axios.post(`/client/${clientId}/insurance`, data);
    return response.data;
}

// Function to add payment card label
export async function addPaymentCardLabel(
    clientId: string,
    paymentCardId: string,
    payload: {
        card_label: string;
    }
) {
    const response = await axios.patch(
        `/client/${clientId}/payment-cards/${paymentCardId}`,
        {
            ...payload,
        }
    );
    return response.data;
}

// Endpoint to fetch card transaction history
export async function fetchCardTransactionHistory(
    clientId: string,
    paymentId: string,
    filters: ICardFilterTypes
) {
    const response = await axios.get(
        `/client/${clientId}/payment-cards/${paymentId}/transactions`,
        {
            params: handleRemoveEmptyParamFromQueryParams(filters),
        }
    );
    return response.data;
}

// Endpoint to fetch client credit cards list
export async function fetchClientCreditCards(clientId: string) {
    const response = await axios.get(`/client/${clientId}/payment-cards`);
    return response.data;
}

// Endpoint to fetch client documents list
export async function fetchClientDocuments(
    filters: IClientDocumentsListFilters
) {
    const response = await axios.get(`/client/${filters.client_id}/documents`, {
        params: handleRemoveEmptyParamFromQueryParams(filters),
    });
    return response.data;
}

// Endpoint to delete client document
export async function deleteClientDocument(
    clientId: string,
    documentId: string
) {
    const response = await axios.delete(
        `/client/${clientId}/documents/${documentId}`
    );

    return response.data;
}

// Endpoint to fetch all insurance providers
export async function fetchInsuranceProviders(
    filters?: IInsuranceProvidersFilterTypes
) {
    const response = await axios.get(`/insurance_providers`, {
        params: handleRemoveEmptyParamFromQueryParams(filters || {}),
    });
    return response.data;
}

// Function to validate payment card
export async function validatePaymentCard(payload: IValidateCardPayload) {
    const response = await axios.post(
        `/client/${payload.clientId}/payment-cards/${payload.paymentCardId}/validate`
    );
    return response.data;
}

// Endpoint to set or reset a client's current payment card
export async function setPreferredPaymentCard(
    payload: ISetPreferredCardPayload
) {
    const response = await axios.patch(
        `/client/${payload.clientId}/payment-cards/${payload.paymentCardId}`,
        { is_preferred: payload.isPreferred }
    );
    return response.data;
}

// Endpoint to edit client insurance details
export async function editClientInsuranceDetails(
    clientId: string,
    insuranceId: string,
    data: IClientEditInsuranceDetails
) {
    const response = await axios.patch(
        `client/${clientId}/insurance/${insuranceId}`,
        data
    );
    return response.data;
}

// Endpoint to delete insurance attachment
export async function deleteInsuranceAttachment(
    clientId: string,
    attachmentId: string
) {
    const response = await axios.delete(
        `/client/${clientId}/insurance/attachment/${attachmentId}`
    );
    return response.data;
}

// Endpoint to fetch client insurance history
export async function fetchClientInsuranceHistory(
    clientId: string,
    page = 1,
    limit = 20
) {
    const response = await axios.get(`/client/${clientId}/insurance`, {
        params: {
            page,
            limit,
        },
    });
    return response.data;
}

export async function fetchClientProviderHistory(
    clientId: string,
    filters: IClientProviderHistoryListFilters
) {
    const response = await axios.get(`/client/${clientId}/provider_history`, {
        params: handleRemoveEmptyParamFromQueryParams(filters),
    });

    return response.data;
}

export async function fetchClientMemos(
    clientId: string,
    filters: IClientMemoListFilterTypes
) {
    const response = await axios.get(`/client/${clientId}/memos`, {
        params: handleRemoveEmptyParamFromQueryParams(filters),
    });

    return response.data;
}

// Function to add new memos
export async function addClientMemo(clientId: string, data: INewMemo) {
    const response = await axios.post(`/client/${clientId}/memos`, data);
    return response.data;
}

export async function fetchCollectionsNotes(
    clientId: string,
    filters: ICollectionsNoteFilterTypes
) {
    const response = await axios.get(`/client/${clientId}/collections-notes`, {
        params: handleRemoveEmptyParamFromQueryParams(filters),
    });
    return response.data;
}

export async function fetchCollectionsNoteActivities(
    clientId: string,
    filters: ICollectionsNoteFilterTypes
) {
    const response = await axios.get(
        `/client/${clientId}/collections-notes/activities`,
        { params: handleRemoveEmptyParamFromQueryParams(filters) }
    );
    return response.data;
}

export async function addCollectionsNote(
    clientId: string,
    data: { description: string }
) {
    const response = await axios.post(
        `/client/${clientId}/collections-notes`,
        data
    );
    return response.data;
}

export async function editCollectionsNote(
    collectionsNoteId: string,
    data: { description: string }
) {
    const response = await axios.patch(
        `/client/collections-note/${collectionsNoteId}`,
        data
    );
    return response.data;
}

export async function deleteCollectionsNote(collectionsNoteId: string) {
    const response = await axios.delete(
        `/client/collections-note/${collectionsNoteId}`
    );
    return response.data;
}

export async function addCollectionsNoteDocuments(
    collectionsNoteId: string,
    formData: FormData
) {
    const response = await axios.post(
        `/client/collections-note/${collectionsNoteId}/documents`,
        formData
    );
    return response.data;
}

export async function deleteCollectionsNoteDocuments(
    collectionsNoteId: string,
    documentIds: string[]
) {
    const response = await axios.delete(
        `/client/collections-note/${collectionsNoteId}/documents`,
        { data: { document_ids: documentIds } }
    );
    return response.data;
}

// Endpoint to fetch client notes list
export async function fetchClientNotes(
    clientId: string,
    filters: IClientNotesListFilterTypes
) {
    const response = await axios.get(`/client/${clientId}/notes`, {
        params: handleRemoveEmptyParamFromQueryParams(filters),
    });

    return response.data;
}

// Endpoint to fetch client fees
export async function fetchClientFees(clientId: string, limit: number) {
    const response = await axios.get(`/client/${clientId}/fees?limit=${limit}`);
    return response.data;
}

export async function editClientCurrentFee(
    clientId: string,
    feeId: string,
    data: IEditClientFee
) {
    const response = await axios.patch(
        `/client/${clientId}/fees/${feeId}`,
        data
    );
    return response.data;
}

// Endpoint to add client flag
export async function addClientFlag(clientId: string, payload: IAddClientFlag) {
    const response = await axios.post(`/client/${clientId}/flags`, payload);
    return response.data;
}

// Endpoint to get all client flags
export async function fetchClientFlags(clientId: string) {
    const response = await axios.get(`/client/${clientId}/flags`);
    return response.data;
}

// Endpoint to get client tags
export async function fetchClientTags(clientId: string) {
    const response = await axios.get(`/client/${clientId}/tags`);
    return response.data;
}

// Endpoint to get client tag activities
export async function fetchClientTagActivities(
    clientId: string,
    filters: IClientTagActivityFilterTypes
) {
    const response = await axios.get(`/client/${clientId}/tags/activities`, {
        params: handleRemoveEmptyParamFromQueryParams(filters),
    });
    return response.data;
}

// Endpoint to add client tag
export async function addClientTag(clientId: string, tagKey: ClientTagKey) {
    const response = await axios.post(`/client/${clientId}/tags`, {
        tag_key: tagKey,
    });
    return response.data;
}

// Endpoint to remove client tag
export async function removeClientTag(clientId: string, tagKey: ClientTagKey) {
    const response = await axios.delete(`/client/${clientId}/tags/${tagKey}`);
    return response.data;
}

// Endpoint to delete client flag
export async function deleteClientFlags(clientId: string, flagId: string) {
    const response = await axios.delete(`/client/${clientId}/flags/${flagId}`);
    return response.data;
}

// Endpoint to update client flag
export async function updateClientFlag(
    clientId: string,
    flagId: string,
    payload: IAddClientFlag
) {
    const response = await axios.patch(
        `/client/${clientId}/flags/${flagId}`,
        payload
    );
    return response.data;
}

// Endpoint to add a session for a client
export async function addClientSession(
    clientId: string,
    payload: IAddClientSession
) {
    const response = await axios.post(`/client/${clientId}/session`, payload);
    return response.data;
}

// Endpoint to edit session for a client
export async function editClientSession(
    clientId: string,
    payload: IEditClientSession
) {
    const response = await axios.patch(`/client/${clientId}/session`, payload);
    return response.data;
}

// Endpoint to delete session
export async function deleteClientSession(
    clientId: string,
    payload: IDeleteClientSession
) {
    const response = await axios.delete(`/client/${clientId}/sessions`, {
        data: payload,
    });
    return response.data;
}

// Delete credit cards
export async function deleteCreditCards(
    clientId: string,
    payload: IDeleteCardsPayload
) {
    const response = await axios.delete(`/client/${clientId}/payment-cards`, {
        data: payload,
    });
    return response.data;
}

export async function deleteClientInsuranceInformation(
    clientId: string,
    payload: IDeleteInsuranceInformationPayload
) {
    const response = await axios.delete(`/client/${clientId}/insurance`, {
        data: payload,
    });
    return response.data;
}

export async function exportClientsList(filters: IClientFilterTypes) {
    const response = await axios.get("/clients/export", {
        params: handleRemoveEmptyParamFromQueryParams(filters),
        responseType: "blob",
    });
    return response.data;
}

export async function clientDataImportDiagnosis(
    payload: IClientIntakeImportDiagnosisPayload
) {
    const response = await axios.post(
        "/data-import/intake/client/diagnose",
        payload
    );
    return response.data;
}

// Endpoint to fetch normalized employer names
export async function fetchNormalizedEmployerNames(
    searchString?: string,
    includeInactive?: boolean
) {
    const response = await axios.get(`/insurance/normalized_employer_names`, {
        params: handleRemoveEmptyParamFromQueryParams({
            search_string: searchString,
            include_inactive: includeInactive,
        }),
    });
    return response.data;
}

export async function generatePaymentLink(
    payload: IGeneratePaymentLinkPayload
) {
    const response = await axios.post(`/payment-link/generate`, payload);
    return response.data;
}

export async function updateIntakeClient(
    intakeClientId: string,
    payload: IUpdateClientOnIntake
) {
    const response = await axios.patch(
        `/intake/client/${intakeClientId}/update`,
        payload
    );
    return response.data;
}

// Endpoint to fetch client's insurance records and their associated fees
export async function fetchClientInsuranceFees(
    clientId: string,
    page?: number,
    limit?: number
) {
    const response = await axios.get(`/client/${clientId}/insurance-fees`, {
        params: handleRemoveEmptyParamFromQueryParams({
            page,
            limit,
        }),
    });
    return response.data;
}

// Endpoint to fetch a client's SMS history from RingCentral
export async function fetchClientSmsHistory(
    clientId: string,
    filters: ISmsHistoryFilters
) {
    const response = await axios.get(`/client/${clientId}/communications/sms`, {
        params: handleRemoveEmptyParamFromQueryParams(filters),
    });
    return response.data;
}

// Endpoint to fetch a client's call history from RingCentral
export async function fetchClientCallHistory(
    clientId: string,
    filters: ICallHistoryFilters
) {
    const response = await axios.get(
        `/client/${clientId}/communications/calls`,
        {
            params: handleRemoveEmptyParamFromQueryParams(filters),
        }
    );
    return response.data;
}

export async function addCommunicationNote(
    clientId: string,
    data: INewCommunicationNoteRecord
) {
    const response = await axios.post(
        `/client/${clientId}/communications/notes`,
        data
    );
    return response.data;
}

export async function editCommunicationNote(
    clientId: string,
    communicationNoteId: string,
    data: { body: string }
) {
    const response = await axios.patch(
        `/client/${clientId}/communications/notes/${communicationNoteId}`,
        data
    );
    return response.data;
}

export async function deleteCommunicationNote(
    clientId: string,
    communicationNoteId: string
) {
    const response = await axios.delete(
        `/client/${clientId}/communications/notes/${communicationNoteId}`
    );
    return response.data;
}
